import { useState, useEffect } from 'react';
import { useQuery, useAction } from 'wasp/client/operations';
import {
  getOrgTypeServiceTypes,
  getServiceTypes,
  linkServiceTypeToOrganizationType,
  unlinkServiceTypeFromOrganizationType,
  updateOrgTypeServiceType
} from 'wasp/client/operations';
import { ChevronDown, ChevronRight, Plus, Trash2, Layers, Save, RotateCcw } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

interface Props {
  organizationTypeId: string;
}

export default function OrgTypeServiceTypesPanel({ organizationTypeId }: Props) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Local pending selections: map from OrgTypeServiceType.id -> string[]
  const [pendingSelections, setPendingSelections] = useState<Map<string, string[]>>(new Map());
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set());

  const { data: linkedServiceTypes = [], isLoading } = useQuery(getOrgTypeServiceTypes, { organizationTypeId });
  const { data: allServiceTypes = [] } = useQuery(getServiceTypes);

  const linkFn = useAction(linkServiceTypeToOrganizationType);
  const unlinkFn = useAction(unlinkServiceTypeFromOrganizationType);
  const updateFn = useAction(updateOrgTypeServiceType);

  // Sync server state into local pending state (only for records not currently being edited)
  useEffect(() => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      for (const lst of linkedServiceTypes as any[]) {
        // Only initialize if not already tracked (preserves in-progress edits)
        if (!next.has(lst.id)) {
          next.set(lst.id, [...lst.selectedServiceDefinitionIds]);
        }
      }
      // Remove entries for unlinked categories
      const linkedIds = new Set((linkedServiceTypes as any[]).map((l: any) => l.id));
      for (const key of next.keys()) {
        if (!linkedIds.has(key)) next.delete(key);
      }
      return next;
    });
  }, [linkedServiceTypes]);

  const linkedServiceTypeIds = new Set((linkedServiceTypes as any[]).map((lst: any) => lst.serviceTypeId));

  const availableServiceTypes = (allServiceTypes as any[]).filter(
    (st: any) => !linkedServiceTypeIds.has(st.id) && st.isActive
  );

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleAddServiceType = async (serviceTypeId: string) => {
    try {
      const result = await linkFn({ organizationTypeId, serviceTypeId }) as any;
      toast({ title: 'Service category added', description: 'All service names pre-selected.' });
      setExpandedIds(prev => new Set([...prev, result.id]));
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to add service category.', variant: 'destructive' });
    }
  };

  const handleRemoveServiceType = async (id: string, label: string) => {
    if (!confirm(`Remove service category "${label}" from this organization type? All service name selections will be lost.`)) return;
    try {
      await unlinkFn({ id });
      setPendingSelections(prev => { const next = new Map(prev); next.delete(id); return next; });
      toast({ title: 'Service category removed', description: `"${label}" unlinked.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to remove service category.', variant: 'destructive' });
    }
  };

  const handleToggle = (lstId: string, defId: string) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      const current = next.get(lstId) ?? [];
      next.set(lstId, current.includes(defId) ? current.filter(id => id !== defId) : [...current, defId]);
      return next;
    });
  };

  const handleSelectAll = (lstId: string, allIds: string[], selectAll: boolean) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(lstId, selectAll ? [...allIds] : []);
      return next;
    });
  };

  const handleSave = async (lst: any) => {
    const selected = pendingSelections.get(lst.id) ?? [];
    setSavingIds(prev => new Set([...prev, lst.id]));
    try {
      await updateFn({ id: lst.id, selectedServiceDefinitionIds: selected });
      toast({ title: 'Saved', description: `Service name selection updated for "${lst.serviceType.label}".` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save.', variant: 'destructive' });
    } finally {
      setSavingIds(prev => { const next = new Set(prev); next.delete(lst.id); return next; });
    }
  };

  const handleReset = (lst: any) => {
    setPendingSelections(prev => {
      const next = new Map(prev);
      next.set(lst.id, [...lst.selectedServiceDefinitionIds]);
      return next;
    });
  };

  const isDirty = (lst: any): boolean => {
    const pending = pendingSelections.get(lst.id);
    if (!pending) return false;
    const saved: string[] = lst.selectedServiceDefinitionIds;
    if (pending.length !== saved.length) return true;
    const savedSet = new Set(saved);
    return pending.some(id => !savedSet.has(id));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Select which service categories and their service names are available for this organization type.
      </p>

      {/* Add Service Category */}
      {availableServiceTypes.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Add Service Category</p>
          <div className="flex flex-wrap gap-2">
            {availableServiceTypes.map((st: any) => (
              <button
                key={st.id}
                onClick={() => handleAddServiceType(st.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {st.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Linked Service Categories */}
      {(linkedServiceTypes as any[]).length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
          <Layers className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No service categories linked yet.</p>
          {availableServiceTypes.length > 0 && (
            <p className="text-gray-400 text-xs mt-1">Use the panel above to add service categories.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {(linkedServiceTypes as any[]).map((lst: any) => {
            const isExpanded = expandedIds.has(lst.id);
            const definitions = lst.serviceType.serviceDefinitions;
            const allDefIds = definitions.map((sd: any) => sd.id);
            const selectedIds = pendingSelections.get(lst.id) ?? lst.selectedServiceDefinitionIds;
            const allSelected = definitions.length > 0 && selectedIds.length === definitions.length;
            const noneSelected = selectedIds.length === 0;
            const dirty = isDirty(lst);
            const saving = savingIds.has(lst.id);

            return (
              <div key={lst.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Category Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                  <button
                    onClick={() => toggleExpand(lst.id)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="font-medium text-gray-900">{lst.serviceType.label}</span>
                    <span className="text-xs text-gray-400 ml-1">
                      {selectedIds.length} / {definitions.length} selected
                    </span>
                    {dirty && (
                      <span className="text-xs text-amber-600 font-medium ml-1">• unsaved</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleRemoveServiceType(lst.id, lst.serviceType.label)}
                    className="ml-3 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>

                {/* Service Definitions */}
                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                    {definitions.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">No active service names in this category.</p>
                    ) : (
                      <>
                        {/* Toolbar */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={() => handleSelectAll(lst.id, allDefIds, true)}
                              disabled={allSelected}
                              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              Select all
                            </button>
                            <button
                              onClick={() => handleSelectAll(lst.id, allDefIds, false)}
                              disabled={noneSelected}
                              className="text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                            >
                              Deselect all
                            </button>
                          </div>
                          {dirty && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleReset(lst)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded transition-colors disabled:opacity-50"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Reset
                              </button>
                              <button
                                onClick={() => handleSave(lst)}
                                disabled={saving}
                                className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                              >
                                <Save className="w-3 h-3" />
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {definitions.map((sd: any) => (
                            <label key={sd.id} className="flex items-center gap-3 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(sd.id)}
                                onChange={() => handleToggle(lst.id, sd.id)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <span className="text-sm text-gray-800 group-hover:text-gray-900">{sd.name}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



